import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import axios from "axios";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "../Planning.css";

const API_BASE = process.env.REACT_APP_API || "";

const EVENT_STATUS_OPTIONS = [
    { value: "pending", label: "Pendiente" },
    { value: "confirmed", label: "Confirmado" },
    { value: "completed", label: "Realizado" },
    { value: "cancelled", label: "Cancelado" },
];

const CREATOR_THEME_PRESETS = [
    {
        soft: "linear-gradient(135deg, rgba(124,58,237,0.12), rgba(168,85,247,0.05))",
        strong: "linear-gradient(135deg, #7c3aed, #a855f7)",
        border: "rgba(124,58,237,0.22)",
        text: "#4c1d95",
        shadow: "rgba(124,58,237,0.18)",
    },
    {
        soft: "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(14,165,233,0.05))",
        strong: "linear-gradient(135deg, #2563eb, #0ea5e9)",
        border: "rgba(37,99,235,0.22)",
        text: "#1d4ed8",
        shadow: "rgba(37,99,235,0.18)",
    },
    {
        soft: "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(52,211,153,0.05))",
        strong: "linear-gradient(135deg, #059669, #10b981)",
        border: "rgba(5,150,105,0.22)",
        text: "#047857",
        shadow: "rgba(5,150,105,0.18)",
    },
    {
        soft: "linear-gradient(135deg, rgba(245,158,11,0.13), rgba(251,191,36,0.05))",
        strong: "linear-gradient(135deg, #d97706, #f59e0b)",
        border: "rgba(217,119,6,0.22)",
        text: "#b45309",
        shadow: "rgba(217,119,6,0.18)",
    },
    {
        soft: "linear-gradient(135deg, rgba(236,72,153,0.12), rgba(244,114,182,0.05))",
        strong: "linear-gradient(135deg, #db2777, #ec4899)",
        border: "rgba(219,39,119,0.22)",
        text: "#be185d",
        shadow: "rgba(219,39,119,0.18)",
    },
    {
        soft: "linear-gradient(135deg, rgba(107,114,128,0.12), rgba(156,163,175,0.05))",
        strong: "linear-gradient(135deg, #4b5563, #6b7280)",
        border: "rgba(75,85,99,0.22)",
        text: "#374151",
        shadow: "rgba(75,85,99,0.18)",
    },
];

const DEFAULT_FORM = {
    id: null,
    title: "",
    description: "",
    start: "",
    end: "",
    status: "pending",
    assignedUsers: [],
};

const animatedComponents = makeAnimated();

const buildUserOption = (item) => ({
    value: String(item.id),
    label: `${item.name} ${item.paternalsur || ""}`.trim(),
    roleLabel: item.rol_name || `Rol ${item.rol}`,
    searchText: `${item.name || ""} ${item.paternalsur || ""} ${item.maternalsur || ""} ${item.rol_name || ""}`.trim(),
});

const formatDateTimeLocal = (dateLike) => {
    if (!dateLike) return "";
    const date = new Date(dateLike);
    if (Number.isNaN(date.getTime())) return "";
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
};

const formatDateLabel = (dateLike, options = {}) => {
    if (!dateLike) return "";
    const date = new Date(dateLike);
    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat("es-PE", {
        weekday: options.weekday ? "long" : undefined,
        day: "numeric",
        month: "long",
        year: options.year ? "numeric" : undefined,
        hour: options.time ? "numeric" : undefined,
        minute: options.time ? "2-digit" : undefined,
        hour12: true,
    }).format(date);
};

const buildStatusDateLabel = (status, statusChangedAt) => {
    if (!["confirmed", "completed", "cancelled"].includes(String(status || ""))) return "";
    if (!statusChangedAt) return "";

    return formatDateLabel(statusChangedAt, { year: true, time: true });
};

const getInitials = (fullName) => {
    const safeName = String(fullName || "").trim();
    if (!safeName) return "EV";
    return safeName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
};

const getCreatorTheme = (key) => {
    const text = String(key || "sin-creador");
    let hash = 0;

    for (let i = 0; i < text.length; i += 1) {
        hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }

    return CREATOR_THEME_PRESETS[Math.abs(hash) % CREATOR_THEME_PRESETS.length];
};

const buildAssignedUsersLabel = (assignedUsers = []) => {
    const names = assignedUsers
        .map((item) => `${item.name || ""} ${item.paternalsur || ""}`.trim())
        .filter(Boolean);

    if (!names.length) return "sin usuarios etiquetados";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} y ${names[1]}`;

    return `${names.slice(0, -1).join(", ")} y ${names[names.length - 1]}`;
};

const buildTimelineText = (eventItem) => {
    const creatorName = eventItem?.extendedProps?.creator?.fullName || eventItem?.extendedProps?.creatorName || "Un usuario";
    const startLabel = formatDateLabel(eventItem.start, { weekday: true, time: true });
    const endLabel = formatDateLabel(eventItem.end, { time: true });
    const createdLabel = formatDateLabel(eventItem.extendedProps.creationDate, { year: true, time: true });
    const assignedUsersLabel = buildAssignedUsersLabel(eventItem.extendedProps.assignedUsers || []);

    return `
        <strong>${creatorName}</strong> creó el evento 
        "<strong>${eventItem.title}</strong>" 
        el ${createdLabel}. 
        Este evento se realizará el ${startLabel} hasta las ${endLabel} 
        y tiene etiquetado a <strong>${assignedUsersLabel}</strong>.
    `;
};

const normalizeCalendarEvent = (item) => {
    const creator = item.creator || {};
    const creatorFullName = creator.fullName || `${creator.name || ""} ${creator.paternalsur || ""}`.trim() || item.creatorName || "";
    const theme = getCreatorTheme(String(creator.id || creatorFullName || item.created_by || item.id));

    return {
        id: String(item.id),
        title: item.title,
        start: item.start_date,
        end: item.end_date,
        backgroundColor: "transparent",
        borderColor: "transparent",
        extendedProps: {
            description: item.description || "",
            status: item.status || "pending",
            assignedUsers: item.assignedUsers || [],
            creatorName: creatorFullName,
            creator,
            creationDate: item.creation_date || item.creationDate || "",
            updateDate: item.update_date || item.updateDate || "",
            statusChangedAt: item.status_changed_at || item.statusChangedAt || "",
            creatorTheme: theme,
            timelineText: "",
        },
    };
};

const Planning = (props) => {
    const [events, setEvents] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(DEFAULT_FORM);
    const [error, setError] = useState("");
    const [timelineReferenceDate, setTimelineReferenceDate] = useState(() => new Date());

    const token = localStorage.getItem("token");
    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("user") || "{}");
        } catch (e) {
            return {};
        }
    }, []);

    const requestConfig = useMemo(() => ({
        headers: {
            Authorization: `Bearer ${token}`,
            user: localStorage.getItem("user") || "",
        },
        withCredentials: true,
    }), [token]);

    const userOptions = useMemo(() => users.map(buildUserOption), [users]);

    const selectedAssignedUsers = useMemo(() => {
        if (!Array.isArray(form.assignedUsers) || !form.assignedUsers.length) return [];

        const selectedMap = new Map(userOptions.map((option) => [option.value, option]));

        return form.assignedUsers
            .map((id) => selectedMap.get(String(id)))
            .filter(Boolean);
    }, [form.assignedUsers, userOptions]);

    const timelineEvents = useMemo(() => {
        const referenceDate = new Date(timelineReferenceDate);
        const referenceYear = referenceDate.getFullYear();
        const referenceMonth = referenceDate.getMonth();
        const referenceDay = referenceDate.getDate();

        return [...events]
            .filter((item) => {
                const creationDate = new Date(item.extendedProps.creationDate || item.start);

                if (Number.isNaN(creationDate.getTime())) return false;

                return (
                    creationDate.getFullYear() === referenceYear &&
                    creationDate.getMonth() === referenceMonth &&
                    creationDate.getDate() === referenceDay
                );
            })
            .sort((a, b) => {
                const aDate = new Date(a.extendedProps.creationDate || a.start).getTime();
                const bDate = new Date(b.extendedProps.creationDate || b.start).getTime();
                return bDate - aDate;
            })
            .map((item) => ({
                ...item,
                extendedProps: {
                    ...item.extendedProps,
                    timelineText: buildTimelineText(item),
                },
            }));
    }, [events, timelineReferenceDate]);

    const reactSelectStyles = useMemo(() => ({
        control: (base, state) => ({
            ...base,
            minHeight: 48,
            borderRadius: 16,
            borderColor: state.isFocused ? "#7c3aed" : "#d9c9f5",
            boxShadow: state.isFocused ? "0 0 0 4px rgba(124, 58, 237, 0.12)" : "none",
            backgroundColor: "#ffffff",
            paddingLeft: 4,
            transition: "all 180ms ease",
            '&:hover': {
                borderColor: "#a855f7",
            },
        }),
        valueContainer: (base) => ({
            ...base,
            paddingTop: 6,
            paddingBottom: 6,
            gap: 6,
        }),
        placeholder: (base) => ({
            ...base,
            color: "#7b728d",
        }),
        multiValue: (base) => ({
            ...base,
            display: "none",
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? "rgba(124, 58, 237, 0.12)" : state.isFocused ? "rgba(124, 58, 237, 0.08)" : "#fff",
            color: "#2f2347",
            cursor: "pointer",
            paddingTop: 12,
            paddingBottom: 12,
        }),
        menu: (base) => ({
            ...base,
            borderRadius: 16,
            overflow: "hidden",
            boxShadow: "0 18px 45px rgba(62, 28, 102, 0.16)",
            border: "1px solid rgba(124, 58, 237, 0.14)",
        }),
        indicatorsContainer: (base) => ({
            ...base,
            paddingRight: 6,
        }),
        clearIndicator: (base) => ({
            ...base,
            color: "#7c3aed",
            '&:hover': {
                color: "#5b21b6",
            },
        }),
        dropdownIndicator: (base, state) => ({
            ...base,
            color: state.isFocused ? "#7c3aed" : "#8c7aa8",
            '&:hover': {
                color: "#7c3aed",
            },
        }),
    }), []);

    const formatUserOptionLabel = useCallback((option) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontWeight: 600 }}>{option.label}</span>
            <span style={{ fontSize: 12, opacity: 0.72 }}>{option.roleLabel}</span>
        </div>
    ), []);

    const loadPlanningData = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const [eventsRes, usersRes] = await Promise.all([
                axios.get(`${API_BASE}/planning`, requestConfig),
                axios.get(`${API_BASE}/planning/users/active`, requestConfig),
            ]);

            const nextEvents = Array.isArray(eventsRes?.data?.data)
                ? eventsRes.data.data.map(normalizeCalendarEvent)
                : [];

            const nextUsers = Array.isArray(usersRes?.data?.data)
                ? usersRes.data.data
                : [];

            setEvents(nextEvents);
            setUsers(nextUsers);
        } catch (err) {
            console.error("Error loading planning data:", err);
            setError("No se pudo cargar la planificación. Intenta nuevamente.");
        } finally {
            setLoading(false);
        }
    }, [requestConfig]);

    useEffect(() => {
        loadPlanningData();
    }, [loadPlanningData]);

    const resetForm = () => {
        setForm(DEFAULT_FORM);
        setModalOpen(false);
    };

    const handleDateSelect = (selectionInfo) => {
        setForm({
            ...DEFAULT_FORM,
            start: formatDateTimeLocal(selectionInfo.start),
            end: formatDateTimeLocal(selectionInfo.end),
        });
        setModalOpen(true);
    };

    const handleEventClick = (clickInfo) => {
        const event = clickInfo.event;
        setForm({
            id: event.id,
            title: event.title || "",
            description: event.extendedProps.description || "",
            start: formatDateTimeLocal(event.start),
            end: formatDateTimeLocal(event.end),
            status: event.extendedProps.status || "pending",
            assignedUsers: (event.extendedProps.assignedUsers || []).map((item) => String(item.user_id || item.id)),
        });
        setModalOpen(true);
    };

    const handleCalendarDatesSet = (dateInfo) => {
        const activeDate = dateInfo?.view?.calendar?.getDate?.() || dateInfo?.start || new Date();
        const normalizedDate = new Date(activeDate);

        if (Number.isNaN(normalizedDate.getTime())) return;

        setTimelineReferenceDate(normalizedDate);
    };

    const handleInputChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleAssignedUsersChange = (selectedOptions) => {
        const values = Array.isArray(selectedOptions)
            ? selectedOptions.map((option) => option.value)
            : [];

        handleInputChange("assignedUsers", values);
    };

    const saveEvent = async () => {
        if (!form.title.trim() || !form.start || !form.end) {
            setError("Título, inicio y fin son obligatorios.");
            return;
        }

        if (new Date(form.start) >= new Date(form.end)) {
            setError("La fecha/hora final debe ser mayor a la inicial.");
            return;
        }

        setSaving(true);
        setError("");

        const payload = {
            title: form.title.trim(),
            description: form.description.trim(),
            start_date: form.start,
            end_date: form.end,
            status: form.status,
            assigned_users: form.assignedUsers,
        };

        try {
            if (form.id) {
                await axios.post(`${API_BASE}/planning/${form.id}`, payload, requestConfig);
            } else {
                await axios.post(`${API_BASE}/planning`, payload, requestConfig);
            }

            await loadPlanningData();
            resetForm();
        } catch (err) {
            console.error("Error saving planning event:", err);
            setError("No se pudo guardar el evento.");
        } finally {
            setSaving(false);
        }
    };

    const deleteEvent = async () => {
        if (!form.id) return;

        const confirmDelete = window.confirm("¿Deseas eliminar este evento?");
        if (!confirmDelete) return;

        setSaving(true);
        setError("");

        try {
            await axios.delete(`${API_BASE}/planning/${form.id}`, requestConfig);
            await loadPlanningData();
            resetForm();
        } catch (err) {
            console.error("Error deleting planning event:", err);
            setError("No se pudo eliminar el evento.");
        } finally {
            setSaving(false);
        }
    };

    const renderEventContent = (eventInfo) => {
        const status = eventInfo.event.extendedProps.status || "pending";
        const statusLabel = EVENT_STATUS_OPTIONS.find((item) => item.value === status)?.label || "Pendiente";
        const creatorName = eventInfo.event.extendedProps.creator?.fullName || eventInfo.event.extendedProps.creatorName || "Sin creador";
        const theme = eventInfo.event.extendedProps.creatorTheme || getCreatorTheme(eventInfo.event.id);

        return (
            <div
                className={`planningEvent planningEvent--${status}`}
                style={{
                    background: theme.soft,
                    border: `1px solid ${theme.border}`,
                    borderLeft: `4px solid ${theme.text}`,
                    borderRadius: 14,
                    boxShadow: `0 10px 22px ${theme.shadow}`,
                    padding: 8,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                    <div className="planningEvent_time">{eventInfo.timeText}</div>
                    <div
                        title={creatorName}
                        style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            background: theme.strong,
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: 0.3,
                            flex: "0 0 auto",
                        }}
                    >
                        {getInitials(creatorName)}
                    </div>
                </div>
                <div className="planningEvent_title">{eventInfo.event.title}</div>
                <div className="planningEvent_status">{statusLabel}</div>
                <div style={{ marginTop: 4, fontSize: 10, fontWeight: 600, color: theme.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    Creó: {creatorName}
                </div>
            </div>
        );
    };

    return (
        <div>
            <style>{`
                @keyframes planningUserTagIn {
                    0% { opacity: 0; transform: translateY(8px) scale(0.96); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes planningTimelineCardIn {
                    0% { opacity: 0; transform: translateY(14px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
            `}</style>
            <Header user={user} namePage={props.namePage} />
            <section className="sectionBody sectionBodyCredit planningSectionBody">
                <div className="containerCustom containerBody planningContainerBody">
                    <div className="planningPageHeader">
                        <div>
                            <h1>Planning</h1>
                            <p>Gestiona actividades, seguimientos, activaciones y tareas del equipo por día, semana o mes.</p>
                        </div>

                        <button
                            type="button"
                            className="planningPrimaryBtn"
                            onClick={() => {
                                setForm(DEFAULT_FORM);
                                setModalOpen(true);
                            }}
                        >
                            + Nuevo evento
                        </button>
                    </div>

                    {error ? <div className="planningAlert">{error}</div> : null}

                    <div className="planningBoard">
                        {loading ? (
                            <div className="planningLoadingCard">Cargando planificación...</div>
                        ) : (
                            <FullCalendar
                                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                locale={esLocale}
                                initialView="timeGridDay"
                                headerToolbar={{
                                    left: "prev,next today",
                                    center: "title",
                                    right: "timeGridDay,timeGridWeek,dayGridMonth",
                                }}
                                buttonText={{
                                    today: "Hoy",
                                    month: "Mes",
                                    week: "Semana",
                                    day: "Día",
                                }}
                                allDaySlot={false}
                                nowIndicator
                                editable={false}
                                selectable
                                selectMirror
                                slotMinTime="07:00:00"
                                slotMaxTime="22:00:00"
                                slotDuration="00:30:00"
                                height="auto"
                                events={events}
                                select={handleDateSelect}
                                eventClick={handleEventClick}
                                eventContent={renderEventContent}
                                datesSet={handleCalendarDatesSet}
                            />
                        )}
                    </div>


                    <div className="planningTimelineSection">
                        <div className="planningTimelineHeader">
                            <div className="planningTimelineHeaderContent">
                                <h2 className="planningTimelineTitle">Actividad del planning creada el {formatDateLabel(timelineReferenceDate, { weekday: true, year: true })}</h2>
                                <p className="planningTimelineSubtitle">
                                    Este historial inferior se sincroniza con la fecha activa del calendario y solo muestra los eventos creados en ese día.
                                </p>
                            </div>

                            <div className="planningTimelineCount">
                                {timelineEvents.length} evento{timelineEvents.length === 1 ? "" : "s"} creado{timelineEvents.length === 1 ? "" : "s"} ese día
                            </div>
                        </div>

                        {!timelineEvents.length ? (
                            <div className="planningLoadingCard">No hay eventos creados el {formatDateLabel(timelineReferenceDate, { weekday: true, year: true })} para mostrar en el historial.</div>
                        ) : (
                            <div className="planningTimelineGrid">
                                {timelineEvents.map((eventItem) => {
                                    const creatorName =
                                        eventItem.extendedProps.creator?.fullName ||
                                        eventItem.extendedProps.creatorName ||
                                        "Sin creador";

                                    const theme =
                                        eventItem.extendedProps.creatorTheme ||
                                        getCreatorTheme(eventItem.id);

                                    const status =
                                        EVENT_STATUS_OPTIONS.find(
                                            (item) => item.value === eventItem.extendedProps.status
                                        )?.label || "Pendiente";

                                    return (
                                        <article
                                            key={eventItem.id}
                                            className="planningTimelineCard"
                                            style={{
                                                "--planning-accent": theme.text,
                                                "--planning-accent-soft": theme.soft,
                                                "--planning-accent-strong": theme.strong,
                                                "--planning-accent-border": theme.border,
                                                "--planning-accent-shadow": theme.shadow,
                                            }}
                                        >
                                            <div className="planningTimelineCardTop">
                                                <div className="planningTimelineAvatar">
                                                    {getInitials(creatorName)}
                                                </div>

                                                <div className="planningTimelineMeta">
                                                    <div className="planningTimelineCreator">{creatorName}</div>
                                                    <div className="planningTimelineDate">
                                                        {formatDateLabel(eventItem.extendedProps.creationDate, {
                                                            weekday: true,
                                                            year: true,
                                                            time: true,
                                                        })}
                                                    </div>
                                                </div>

                                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                                    <span className={`planningTimelineStatus planningTimelineStatus--${eventItem.extendedProps.status}`}>
                                                        {status}
                                                    </span>

                                                    {buildStatusDateLabel(eventItem.extendedProps.status, eventItem.extendedProps.statusChangedAt) ? (
                                                        <span
                                                            style={{
                                                                fontSize: 11,
                                                                fontWeight: 700,
                                                                color: theme.text,
                                                                background: "rgba(255,255,255,0.72)",
                                                                border: `1px solid ${theme.border}`,
                                                                borderRadius: 999,
                                                                padding: "5px 10px",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {buildStatusDateLabel(eventItem.extendedProps.status, eventItem.extendedProps.statusChangedAt)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>

                                            <div className="planningTimelineCardBody">
                                                <h3 className="planningTimelineEventTitle">{eventItem.title}</h3>

                                                <div
                                                    className="planningTimelineDescription"
                                                    dangerouslySetInnerHTML={{ __html: eventItem.extendedProps.timelineText }}
                                                />


                                                <div className="planningTimelineDescription">
                                                    <div className="planningTimelineDates">
                                                        <span className="planningTimelineDateItem">
                                                            <strong>Inicio:</strong>{" "}
                                                            {formatDateLabel(eventItem.start, { weekday: true, time: true })}
                                                        </span>

                                                        <span className="planningTimelineDateItem">
                                                            <strong>Fin:</strong>{" "}
                                                            {formatDateLabel(eventItem.end, { time: true })}
                                                        </span>
                                                    </div>

                                                    {!!eventItem.extendedProps.description && (
                                                        <div className="planningTimelineDescriptionText">
                                                            <strong>Descripción:</strong> {eventItem.extendedProps.description}
                                                        </div>
                                                    )}
                                                </div>

                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </div>



                </div>
            </section>

            {modalOpen ? (
                <div className="planningModalOverlay" onClick={resetForm}>
                    <div className="planningModal" onClick={(e) => e.stopPropagation()}>
                        <div className="planningModalHeader">
                            <div>
                                <h2>{form.id ? "Editar evento" : "Nuevo evento"}</h2>
                                <p>Programa actividades y asigna responsables activos.</p>
                            </div>
                            <button type="button" className="planningCloseBtn" onClick={resetForm}>×</button>
                        </div>

                        <div className="planningFormGrid">
                            <div className="planningField planningField--full">
                                <label>Título</label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => handleInputChange("title", e.target.value)}
                                    placeholder="Ejemplo: Activación en tienda central"
                                />
                            </div>

                            <div className="planningField">
                                <label>Inicio</label>
                                <input
                                    type="datetime-local"
                                    value={form.start}
                                    onChange={(e) => handleInputChange("start", e.target.value)}
                                />
                            </div>

                            <div className="planningField">
                                <label>Fin</label>
                                <input
                                    type="datetime-local"
                                    value={form.end}
                                    onChange={(e) => handleInputChange("end", e.target.value)}
                                />
                            </div>

                            <div className="planningField">
                                <label>Estado</label>
                                <select
                                    value={form.status}
                                    onChange={(e) => handleInputChange("status", e.target.value)}
                                >
                                    {EVENT_STATUS_OPTIONS.map((status) => (
                                        <option key={status.value} value={status.value}>
                                            {status.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="planningField planningField--full">
                                <label>Usuarios etiquetados</label>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    components={animatedComponents}
                                    value={selectedAssignedUsers}
                                    onChange={handleAssignedUsersChange}
                                    options={userOptions}
                                    styles={reactSelectStyles}
                                    classNamePrefix="planningReactSelect"
                                    placeholder="Selecciona uno o más usuarios"
                                    noOptionsMessage={() => "No se encontraron usuarios"}
                                    formatOptionLabel={formatUserOptionLabel}
                                    isClearable
                                />

                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 10,
                                        marginTop: 14,
                                        minHeight: selectedAssignedUsers.length ? "auto" : 0,
                                    }}
                                >
                                    {selectedAssignedUsers.map((item, index) => (
                                        <div
                                            key={item.value}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                                padding: "6px 8px",
                                                borderRadius: 14,
                                                background: "linear-gradient(135deg, rgba(124,58,237,0.14), rgba(168,85,247,0.08))",
                                                border: "1px solid rgba(124,58,237,0.14)",
                                                boxShadow: "0 8px 20px rgba(124,58,237,0.10)",
                                                transform: "translateY(0)",
                                                animation: `planningUserTagIn 220ms ease ${index * 35}ms both`,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: "50%",
                                                    background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: 700,
                                                    fontSize: 13,
                                                    letterSpacing: 0.3,
                                                    boxShadow: "0 8px 18px rgba(124,58,237,0.22)",
                                                }}
                                            >
                                                {item.label.split(" ").map((part) => part[0]).slice(0, 2).join("").toUpperCase()}
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, fontSize: 13 }}>
                                                <span style={{ fontWeight: 700, color: "#2f2347" }}>{item.label}</span>
                                                <span style={{ fontSize: 12, color: "#6d5b8d" }}>{item.roleLabel}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="planningField planningField--full">
                                <label>Descripción</label>
                                <textarea
                                    rows="4"
                                    value={form.description}
                                    onChange={(e) => handleInputChange("description", e.target.value)}
                                    placeholder="Detalle del evento, seguimiento comercial, activación, capacitación o tarea interna."
                                />
                            </div>
                        </div>

                        <div className="planningModalFooter">
                            {form.id ? (
                                <button type="button" className="planningDangerBtn" onClick={deleteEvent} disabled={saving}>
                                    Eliminar
                                </button>
                            ) : <span />}

                            <div className="planningModalActions">
                                <button type="button" className="planningGhostBtn" onClick={resetForm} disabled={saving}>
                                    Cancelar
                                </button>
                                <button type="button" className="planningPrimaryBtn" onClick={saveEvent} disabled={saving}>
                                    {saving ? "Guardando..." : form.id ? "Actualizar" : "Guardar evento"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default Planning;
